import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Rocket, Plus, Loader2, Sparkles, Coins, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { getCurrentAccount } from '../../lib/auth';
import MainLayout from '../../components/MainLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import TopUpDialog from '../../components/credits/TopUpDialog';

const FIRST_VISIT_KEY = 'migration_first_time_tooltip_dismissed';

const STATUS_STYLE = {
  intake: 'border-slate-400 text-slate-600',
  in_progress: 'border-blue-500 text-blue-700',
  review: 'border-amber-500 text-amber-700',
  complete: 'border-emerald-500 text-emerald-700',
  cancelled: 'border-rose-400 text-rose-600',
};

export default function MigrationJourneysPage() {
  const navigate = useNavigate();
  const account = getCurrentAccount();
  const [journeys, setJourneys] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [platform, setPlatform] = useState('');
  const [creating, setCreating] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [topupOpen, setTopupOpen] = useState(false);
  const [tooltipDismissed, setTooltipDismissed] = useState(
    typeof window !== 'undefined' && localStorage.getItem(FIRST_VISIT_KEY) === '1',
  );

  const params = { params: { account_id: account?.id } };

  useEffect(() => {
    if (!account?.id) return;
    api.get('/ubm-migration/journeys', params)
      .then(({ data }) => setJourneys(data.journeys || []))
      .catch(() => setJourneys([]));
    api.get('/credits/wallet', params)
      .then(({ data }) => setWallet(data))
      .catch(() => setWallet(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.id]);

  const dismissTooltip = () => {
    localStorage.setItem(FIRST_VISIT_KEY, '1');
    setTooltipDismissed(true);
  };

  const showFirstTimeBanner = !tooltipDismissed
    && wallet
    && (wallet.balance_credits ?? 0) < 5
    && (wallet.purchased_credits ?? 0) === 0
    && Array.isArray(journeys)
    && journeys.length === 0;

  const createJourney = async () => {
    setCreating(true);
    try {
      const { data } = await api.post('/ubm-migration/journeys',
        { platform: platform || null, has_export_files: true }, params);
      toast.success('Migration journey created');
      navigate(`/ubm/migration/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create journey');
    } finally {
      setCreating(false);
    }
  };

  return (
    <MainLayout>
      <div className="max-w-5xl mx-auto space-y-4" data-testid="migration-journeys-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Rocket className="h-6 w-6 text-orange-600" /> AI Migration
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Migrate utility onboarding data from any incumbent UBM platform — guided, reviewed, reversible.
            </p>
          </div>
          <Button className="rounded-none bg-orange-600 hover:bg-orange-700" onClick={() => setShowNew(true)} data-testid="migration-new-journey-btn">
            <Plus className="h-4 w-4 mr-1" /> New Migration
          </Button>
        </div>

        <div className="rounded-md border border-orange-200 bg-orange-50 p-3 text-sm text-slate-700 flex items-start gap-2" data-testid="migration-astrid-tip">
          <Sparkles className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
          <span>Fastest path: open the AI assistant and say <em>"I want to migrate from Conservice"</em> (or any platform) — it will guide you through the whole journey, including file imports and review.</span>
        </div>

        {showFirstTimeBanner && (
          <div
            className="relative rounded-md border-2 border-emerald-300 bg-gradient-to-r from-emerald-50 to-amber-50 p-4 pr-10"
            data-testid="migration-first-time-banner"
          >
            <button
              type="button"
              aria-label="Dismiss"
              onClick={dismissTooltip}
              className="absolute top-2 right-2 text-slate-400 hover:text-slate-600"
              data-testid="migration-first-time-dismiss"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 shrink-0 rounded-none bg-emerald-500 text-white flex items-center justify-center">
                <Coins className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-bold text-slate-900">
                  Test your AI migration agent for just $5
                </div>
                <p className="text-xs text-slate-600 mt-1">
                  The sample scrape (~5 properties) is <strong>free</strong>. To onboard a full portfolio,
                  top up starting at $5 — that&apos;s 50 credits, enough to migrate ~30 properties end-to-end.
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <Button
                    size="sm"
                    className="rounded-none bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => setTopupOpen(true)}
                    data-testid="migration-first-time-topup-btn"
                  >
                    <Coins className="h-3.5 w-3.5 mr-1" /> Top up $5
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-none border-emerald-500 text-emerald-700 hover:bg-emerald-100"
                    onClick={dismissTooltip}
                    data-testid="migration-first-time-later-btn"
                  >
                    Maybe later
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {journeys === null ? (
          <div className="text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : journeys.length === 0 ? (
          <div className="rounded-md border border-dashed p-10 text-center text-sm text-slate-500" data-testid="migration-empty-state">
            No migration journeys yet.
          </div>
        ) : (
          <div className="border rounded-md divide-y bg-white">
            {journeys.map((j) => (
              <button key={j.id} className="w-full flex items-center gap-3 p-3 text-left hover:bg-slate-50"
                onClick={() => navigate(`/ubm/migration/${j.id}`)} data-testid={`migration-journey-row-${j.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{j.plan?.source_platform || 'Untitled migration'}</div>
                  <div className="text-xs text-slate-400">Created {new Date(j.created_at).toLocaleDateString()}</div>
                </div>
                <div className="text-xs text-slate-500">
                  {j.counts?.all?.total || 0} staged
                </div>
                <Badge variant="outline" className={`rounded-none capitalize ${STATUS_STYLE[j.status] || ''}`}>
                  {(j.status || '').replace('_', ' ')}
                </Badge>
              </button>
            ))}
          </div>
        )}

        <Dialog open={showNew} onOpenChange={setShowNew}>
          <DialogContent>
            <DialogHeader><DialogTitle>New Migration Journey</DialogTitle></DialogHeader>
            <div className="space-y-2">
              <label className="text-sm font-medium">Which platform are you migrating from?</label>
              <Input value={platform} onChange={(e) => setPlatform(e.target.value)}
                placeholder="e.g. Conservice, Constellation, Engie, Arcadia…" data-testid="migration-new-platform-input" />
              <p className="text-xs text-slate-500">You'll upload your export files (XLS/CSV) on the next screen. All data is staged for your review before anything is onboarded.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setShowNew(false)}>Cancel</Button>
              <Button className="rounded-none bg-orange-600 hover:bg-orange-700" onClick={createJourney} disabled={creating} data-testid="migration-new-create-btn">
                {creating && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <TopUpDialog open={topupOpen} onOpenChange={setTopupOpen} wallet={wallet} />
      </div>
    </MainLayout>
  );
}
